import { useQuery } from "@tanstack/react-query";
import { apiService } from "@/services";
import type { KeywordTrendMatchMode, KeywordTrendsResponse } from "@/types/keyword-trends";

export const keywordTrendsQueryKey = (brandId: number | null, term: string, mode: KeywordTrendMatchMode) =>
  ["keyword-trends", brandId, mode, term] as const;

/**
 * AUT-429. Enabled only once a term has been submitted: this hits the warehouse, so it must not
 * fire per keystroke. Results are cached per (brand, term, mode) -- the searchable universe is the
 * brand's own keyword bank, so two brands asking the same term are two different questions.
 */
export const useKeywordTrends = (
  brandId: number | null,
  term: string,
  mode: KeywordTrendMatchMode,
  enabled: boolean,
) =>
  useQuery({
    queryKey: keywordTrendsQueryKey(brandId, term, mode),
    enabled: enabled && brandId != null && term.trim().length > 0,
    staleTime: 5 * 60 * 1000,
    // Opts out of the global 5xx retry (`query-client.ts`). Every attempt here is a fresh ~12 s
    // scan on a billed warehouse, and a warehouse failure is not the transient kind a retry fixes
    // -- the cost vector on this endpoint is spend, not availability.
    retry: false,
    queryFn: () =>
      apiService.get<KeywordTrendsResponse, { brand_id: number; term: string; mode: KeywordTrendMatchMode }>(
        "/keyword-trends",
        { brand_id: brandId as number, term: term.trim(), mode },
      ),
  });

/**
 * How much has to be typed before the type-ahead asks for anything.
 *
 * Mirrors `KeywordTrendsService::SUGGESTION_MIN_CHARS`, which enforces it -- this only avoids the
 * round trip. An empty box used to list the alphabetically first keywords in the brand's bank
 * (`$1`, `$1 deals`, `$1 item`…), which reads as a broken feature rather than a suggestion.
 */
export const SUGGESTION_MIN_CHARS = 2;

export const keywordSuggestionsQueryKey = (brandId: number | null, term: string) =>
  ["keyword-trends", "suggestions", brandId, term] as const;

/**
 * AUT-429 type-ahead. Unlike `useKeywordTrends` this one **may** fire per keystroke: it reads the
 * brand's keyword bank (layer B) and never touches the share, so it costs no warehouse compute.
 *
 * The design reference hands its combobox an array of every keyword in the universe. That works for
 * a mock and not for a real brand, which carries 1k-10k keywords per product across several
 * products -- so the interaction is ported and the data source is not.
 */
export const useKeywordSuggestions = (brandId: number | null, term: string, enabled: boolean) =>
  useQuery({
    queryKey: keywordSuggestionsQueryKey(brandId, term),
    enabled: enabled && brandId != null && term.trim().length >= SUGGESTION_MIN_CHARS,
    staleTime: 5 * 60 * 1000,
    // `placeholderData: keepPreviousData` is not set here because `query-client.ts` already sets it
    // globally -- which is what keeps the previous suggestions on screen while the next keystroke's
    // query loads, rather than emptying the list and closing the popover between every character.
    // A suggestion that never arrives is a control that quietly does nothing, so unlike the search
    // this one takes the global retry policy.
    queryFn: () =>
      apiService.get<{ suggestions: string[] }, { brand_id: number; term: string }>(
        "/keyword-trends/suggestions",
        { brand_id: brandId as number, term: term.trim() },
      ),
  });
